import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import useEscapeKey from "@hooks/useEscapeKey";
import TableForUIPagination from "../Tables/TableForUIPagination";
import { createPortal } from "react-dom";

const LicenseConflictModal = ({ isOpen, onClose, conflictData = [], "data-testid": dataTestId = "license-conflict-modal" }) => {
    const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
    const { t } = useTranslation();
    const { colors } = useTheme();
    useEscapeKey(onClose, isOpen);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const totalData = conflictData?.length || 0;
    const totalPages = Math.max(1, Math.ceil(totalData / itemsPerPage));

    const tableData = useMemo(() => {
        return (conflictData || []).map((item, index) => ({
            order: index + 1,
            component: item.component || "---",
            componentLicense: item.componentLicense || "---",
        }));
    }, [conflictData]);

    const columns = ["order", "component", "componentLicense"];
    const columnConfig = {
        order: {
            style: { width: "15%" },
            headerRender: () => t("common.order"),
        },
        component: {
            style: { width: "45%", whiteSpace: "normal" },
            headerRender: () => t("project.license-conflict-modal.component-2"),
        },
        componentLicense: {
            style: { width: "40%", whiteSpace: "normal" },
            headerRender: () => t("project.license-conflict-modal.component-2-license"),
        },
    };

    const handleItemsPerPageChange = (value) => {
        setItemsPerPage(value);
        setCurrentPage(1);
    };

    // Reset page when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setCurrentPage(1);
        }
    }, [isOpen]);

    if (!isOpen) return null;
    const modalContent = (
        <div
            className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start md:justify-center p-4 backdrop-blur-[1px] overflow-x-auto`}
            data-testid={`${dataTestId}-overlay`}
        >
            <div className={`${colors.bgModal} max-h-[80vh] w-[700px] min-w-[700px] flex-shrink-0 overflow-auto rounded-lg shadow-xl`} data-testid={dataTestId}>
                {/* Header */}
                <div className={`px-6 py-4 ${colors.borderPrimary} border-b`} data-testid={`${dataTestId}-header`}>
                    <div className="flex items-center justify-between">
                        <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>
                            {t("project.license-conflict-modal.title")}
                        </h2>
                        <button
                            data-testid={`${dataTestId}-close-button`}
                            onClick={onClose}
                            className={`p-2 ${colors.textMuted} hover:${colors.textPrimary} transition-colors`}
                            aria-label="Close"
                        >
                            <span className="text-3xl font-bold leading-none">×</span>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="px-6 py-4" data-testid={`${dataTestId}-content`}>
                    {totalData > 0 ? (
                        <div data-testid={`${dataTestId}-table`}>
                        <TableForUIPagination
                            columns={columns}
                            columnConfig={columnConfig}
                            data={tableData}
                            loading={false}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            totalPages={totalPages}
                            totalData={totalData}
                            maxRowsPerPage={itemsPerPage}
                            onItemsPerPageChange={handleItemsPerPageChange}
                            showPagination={totalPages > 1}
                            shouldShowGoToPage={true}
                            tableStyle=""
                        />
                        </div>
                    ) : (
                        <div className={`text-center py-8 ${colors.textSecondary}`} data-testid={fieldValueTestId("no-data")}>
                            {t("project.license-conflict-modal.no-data")}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
    return createPortal(modalContent, document.body);
};

export default LicenseConflictModal; 